import BacklogIcon from '@atlaskit/icon/core/backlog';
import BoardIcon from '@atlaskit/icon/core/board';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import ListChecklistIcon from '@atlaskit/icon/core/list-checklist';
import PageIcon from '@atlaskit/icon/core/page';
import TimelineIcon from '@atlaskit/icon/core/timeline';

import {
	USER_SEGMENTATION_TEAM_TYPE_MARKETING,
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
	USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_DESIGN,
	USER_SEGMENTATION_TEAM_TYPE_SALES,
} from '../../models/user-segmentation';

import { DocSvg } from './assets/doc.tsx';
import { MyDesignProjectSvg } from './assets/myDesignProject.tsx';
import { MyMarketingCampaignsSvg } from './assets/myMarketingCampaigns.tsx';
import { MyProductRoadmapSvg } from './assets/myProductRoadmap.tsx';
import { MyProjectSvg } from './assets/myProject.tsx';
import { MySalesPipelineSvg } from './assets/mySalesPipeline.tsx';
import { MySoftwareProjectSvg } from './assets/mySoftwareProject.tsx';
import { ProjectTrackerSvg } from './assets/projectTracker.tsx';
import { WhiteboardSvg } from './assets/whiteboard.tsx';
import { messages } from './messages';
import { TeamType, WorkOption } from './types';

const TeamTypesWithFlow = new Set([
	USER_SEGMENTATION_TEAM_TYPE_MARKETING,
	USER_SEGMENTATION_TEAM_TYPE_SOFTWARE,
	USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT,
	USER_SEGMENTATION_TEAM_TYPE_DESIGN,
	USER_SEGMENTATION_TEAM_TYPE_SALES,
]);

export const getTeamTypeWithFlow = (teamType: string | undefined): TeamType => {
	if (teamType && TeamTypesWithFlow.has(teamType as TeamType)) {
		return teamType as TeamType;
	}
	return 'default';
};

const marketingWorkOptions: WorkOption[] = [
	{
		id: 'campaigns',
		name: messages.campaigns,
		icon: BoardIcon,
		redirect: 'jira',
	},
	{
		id: 'events',
		name: messages.events,
		icon: CalendarIcon,
		redirect: 'jira',
	},
	{
		id: 'project-management',
		name: messages.projectManagement,
		icon: ListChecklistIcon,
		redirect: 'jira',
	},
	{
		id: 'content-strategy',
		name: messages.contentStrategy,
		icon: PageIcon,
		redirect:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:content-strategy-blueprint',
		templateTitle: 'Content+strategy',
	},
	{
		id: 'social-media-plans',
		name: messages.socialMediaPlans,
		icon: PageIcon,
		redirect: 'com.atlassian.confluence.plugins.confluence-business-blueprints:marketing-blueprint',
		templateTitle: 'Marketing+plan',
	},
	{
		id: 'goals-tracker',
		name: messages.goalsTracker,
		icon: PageIcon,
		redirect: 'com.atlassian.confluence.plugins.confluence-business-blueprints:gsm-blueprint',
		templateTitle: 'Goals',
	},
];

const softwareDevelopmentWorkOptions: WorkOption[] = [
	{
		id: 'task-tracking',
		name: messages.taskTracking,
		icon: TimelineIcon,
		redirect: 'jira',
	},
	{
		id: 'bug-tracking',
		name: messages.bugTracking,
		icon: BoardIcon,
		redirect: 'jira',
	},
	{
		id: 'project-management',
		name: messages.projectManagement,
		icon: ListChecklistIcon,
		redirect: 'jira',
	},
	{
		id: 'product-roadmap',
		name: messages.productRoadmap,
		icon: PageIcon,
		redirect:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:software-architecture-review-blueprint',
		templateTitle: 'Software+architecture+review',
	},
	{
		id: 'goals-tracker',
		name: messages.goalsTracker,
		icon: PageIcon,
		redirect: 'com.atlassian.confluence.plugins.confluence-business-blueprints:gsm-blueprint',
		templateTitle: 'Goals',
	},
	{
		id: 'how-to-article',
		name: messages.howToArticle,
		icon: PageIcon,
		redirect:
			'com.atlassian.confluence.plugins.confluence-knowledge-base:kb-how-to-article-blueprint',
		templateTitle: 'How+To',
	},
];

const productManagementWorkOptions: WorkOption[] = [
	{
		id: 'product-roadmap',
		name: messages.productRoadmap,
		icon: TimelineIcon,
		redirect: 'jira',
	},
	{
		id: 'project-management',
		name: messages.projectManagement,
		icon: ListChecklistIcon,
		redirect: 'jira',
	},
	{
		id: 'feature-requests',
		name: messages.featureRequests,
		icon: BoardIcon,
		redirect: 'jira',
	},
	{
		id: 'product-requirements',
		name: messages.productRequirements,
		icon: PageIcon,
		redirect:
			'com.atlassian.confluence.plugins.confluence-space-blueprints:product-requirements-template',
		templateTitle: 'Product+requirements',
	},
	{
		id: 'how-to-article',
		name: messages.howToArticle,
		icon: PageIcon,
		redirect:
			'com.atlassian.confluence.plugins.confluence-knowledge-base:kb-how-to-article-blueprint',
		templateTitle: 'How+To',
	},
	{
		id: 'goals-tracker',
		name: messages.goalsTracker,
		icon: CalendarIcon,
		redirect: 'com.atlassian.confluence.plugins.confluence-business-blueprints:gsm-blueprint',
		templateTitle: 'Goals',
	},
];

const designWorkOptions: WorkOption[] = [
	{
		id: 'creative-projects',
		name: messages.creativeProjects,
		icon: TimelineIcon,
		redirect: 'jira',
	},
	{
		id: 'design-process',
		name: messages.designProcess,
		icon: BoardIcon,
		redirect: 'jira',
	},
	{
		id: 'design-sprint',
		name: messages.designSprint,
		icon: PageIcon,
		redirect:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:design-sprint-blueprint',
		templateTitle: 'Design+sprint',
	},
	{
		id: 'meeting-notes',
		name: messages.meetingNotes,
		icon: PageIcon,
		redirect:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:meeting-notes-blueprint',
		templateTitle: 'Meeting+notes',
	},
	{
		id: 'design-decisions',
		name: messages.designDecisions,
		icon: PageIcon,
		redirect:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:design-decision-blueprint',
		templateTitle: 'Design+decision',
	},
	{
		id: 'message-house',
		name: messages.messageHouse,
		icon: PageIcon,
		redirect:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:message-house-template-blueprint',
		templateTitle: 'Message+house',
	},
];

const salesWorkOptions: WorkOption[] = [
	{
		id: 'pipeline-tracking',
		name: messages.pipelineTracking,
		icon: BoardIcon,
		redirect: 'jira',
	},
	{
		id: 'lead-tracking',
		name: messages.leadTracking,
		icon: BacklogIcon,
		redirect: 'jira',
	},
	{
		id: 'project-management',
		name: messages.projectManagement,
		icon: ListChecklistIcon,
		redirect: 'jira',
	},
	{
		id: 'strategic-plan',
		name: messages.strategicPlan,
		icon: PageIcon,
		redirect:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:business-strategy-blueprint',
		templateTitle: 'Strategic+plan',
	},
	{
		id: 'meeting-notes',
		name: messages.meetingNotes,
		icon: PageIcon,
		redirect:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:meeting-notes-blueprint',
		templateTitle: 'Meeting+notes',
	},
	{
		id: 'goals-tracker',
		name: messages.goalsTracker,
		icon: CalendarIcon,
		redirect: 'com.atlassian.confluence.plugins.confluence-business-blueprints:gsm-blueprint',
		templateTitle: 'Goals',
	},
];

const defaultWorkOptions: WorkOption[] = [
	{
		id: 'project-management',
		name: messages.projectManagement,
		icon: ListChecklistIcon,
		redirect: 'jira',
	},
	{
		id: 'process-management',
		name: messages.processManagement,
		icon: BoardIcon,
		redirect: 'jira',
	},
	{
		id: 'task-tracking',
		name: messages.taskTracking,
		icon: BoardIcon,
		redirect: 'jira',
	},
	{
		id: 'goals-tracker',
		name: messages.goalsTracker,
		icon: PageIcon,
		redirect: 'com.atlassian.confluence.plugins.confluence-business-blueprints:gsm-blueprint',
		templateTitle: 'Goals',
	},
	{
		id: 'strategic-plan',
		name: messages.strategicPlan,
		icon: PageIcon,
		redirect:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:business-strategy-blueprint',
		templateTitle: 'Strategic+plan',
	},
	{
		id: 'project-plan',
		name: messages.projectPlan,
		icon: PageIcon,
		redirect:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:projectplan-blueprint',
		templateTitle: 'Project+plan',
	},
];

export const aiLedOnboardingWorkOptions: WorkOption[] = [
	{
		id: 'project-tracker',
		name: messages.projectTracker,
		icon: CalendarIcon,
		redirect: 'jira',
	},
	{
		id: 'doc',
		name: messages.doc,
		icon: BoardIcon,
		redirect: 'live-doc',
	},
	{
		id: 'whiteboard',
		name: messages.whiteboard,
		icon: ListChecklistIcon,
		redirect: 'whiteboard',
	},
];

export const getWorkOptionsByTeamType = (teamType: TeamType): WorkOption[] => {
	switch (teamType) {
		case USER_SEGMENTATION_TEAM_TYPE_MARKETING:
			return marketingWorkOptions;
		case USER_SEGMENTATION_TEAM_TYPE_SOFTWARE:
			return softwareDevelopmentWorkOptions;
		case USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT:
			return productManagementWorkOptions;
		case USER_SEGMENTATION_TEAM_TYPE_DESIGN:
			return designWorkOptions;
		case USER_SEGMENTATION_TEAM_TYPE_SALES:
			return salesWorkOptions;
		default:
			return defaultWorkOptions;
	}
};

export const getImageByTeamType = (teamType: TeamType): (() => JSX.Element) => {
	switch (teamType) {
		case USER_SEGMENTATION_TEAM_TYPE_MARKETING:
			return MyMarketingCampaignsSvg;
		case USER_SEGMENTATION_TEAM_TYPE_SOFTWARE:
			return MySoftwareProjectSvg;
		case USER_SEGMENTATION_TEAM_TYPE_PRODUCT_MANAGEMENT:
			return MyProductRoadmapSvg;
		case USER_SEGMENTATION_TEAM_TYPE_DESIGN:
			return MyDesignProjectSvg;
		case USER_SEGMENTATION_TEAM_TYPE_SALES:
			return MySalesPipelineSvg;
		default:
			return MyProjectSvg;
	}
};

export const getAssetByContentType = (contentType: string | null): (() => JSX.Element) => {
	switch (contentType) {
		case 'doc':
			return DocSvg;
		case 'whiteboard':
			return WhiteboardSvg;
		case 'project-tracker':
		default:
			return ProjectTrackerSvg;
	}
};
